import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = "";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Person being viewed in the network (impersonation). Persisted in
  // sessionStorage so the impersonation survives full-page reloads or
  // navigations via legacy `<a href>` links (only cleared on logout,
  // explicit reset, or new tab).
  const [viewingUser, _setViewingUser] = useState(() => {
    try {
      const raw = window.sessionStorage.getItem('ihtp_viewing_user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const setViewingUser = useCallback((profile) => {
    _setViewingUser(profile);
    try {
      if (profile) window.sessionStorage.setItem('ihtp_viewing_user', JSON.stringify(profile));
      else window.sessionStorage.removeItem('ihtp_viewing_user');
    } catch { /* ignore */ }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      if (data && data.ID_Cliente) {
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    // Unified login — sets BOTH cookies (my_access_token always, access_token
    // when user is entitled to CRM). Returns the areas the user can enter so
    // the frontend can route to the selector or directly into the only area.
    const { data } = await axios.post(`${API}/api/unified/login`, { email, password }, { withCredentials: true });
    if (data.areas?.includes('crm')) {
      // Fetch full profile for the CRM contexts that expect it
      const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(me.data);
    } else {
      // Client only — no CRM profile available. Keep user=false so guarded
      // CRM routes redirect to /areas (the selector handles MY/MEDIA).
      setUser(false);
    }
    setViewingUser(null);
    return { user: data.user, areas: data.areas };
  };

  const logout = async () => {
    // Single call clears access_token + refresh_token + my_access_token +
    // media_access_token so the user is signed out from MY, MEDIA and CRM at
    // once. Admin cookie (ihtp_admin_session) is untouched — admin flow is
    // independent.
    try {
      await axios.post(`${API}/api/unified/logout`, {}, { withCredentials: true });
    } catch { /* already gone is fine */ }
    try { window.localStorage.removeItem('ihtp_last_area'); } catch { /* ignore */ }
    setUser(false);
    setViewingUser(null);
  };

  // Switch to viewing another person in the network
  const switchUser = async (id) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/viewAs`, { ID_Cliente: id }, { withCredentials: true });
      if (data.success && data.profile) {
        setViewingUser(data.profile);
        return data.profile;
      }
    } catch {}
    return null;
  };

  // Reset back to own view
  const resetView = () => {
    setViewingUser(null);
  };

  // The active ID used by all API calls
  const activeId = viewingUser ? viewingUser.ID_Cliente : (user ? user.ID_Cliente : null);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, viewingUser, switchUser, resetView, activeId }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
